import {useEffect, useState} from 'react';
import cx from 'classnames';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {useLifestylePreview} from '#apollo/schema';
import {Screen, Text} from '#component/base';
import {SubscriptionCTACard} from '#component/partial';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {pageSlideInKeyframe} from '#style/keyframes.css';
import {sprinkles} from '#style/sprinkles';
import {getNormalizedLifestyleScore} from '#util/lifestyleScore';

import {CategoryList, Header, NavHeader} from './component';
import {getPreviewCategories} from './LifestylePreview.util';

import * as css from './LifestylePreview.css';

export const LifestylePreviewScreen = () => {
  const [play, setPlay] = useState(false);
  const {data, loading} = useLifestylePreview();
  const {replace} = useRouter();
  const {subscription, mostRecentLabSubmission} = data?.me || {};
  const {hasActiveSubscription} = subscription || {};
  const {registeredDate, userSurveySubmission} = mostRecentLabSubmission || {};
  const {categories, lifestyleScore, maxLifestyleScore, surveyId, surveyKey} =
    userSurveySubmission?.surveyAssessment || {};
  const hasSurveyAssessment = !!userSurveySubmission?.surveyAssessment;
  const previewCategories = getPreviewCategories(categories);

  if (!loading && !hasSurveyAssessment) {
    // If the user has no surveySubmission for their most recent labSubmission,
    // there's no results to preview, so redirect them to the /mytallyage.
    replace('/mytallyage');
  }

  useEffect(() => {
    if (!loading && typeof lifestyleScore === 'number') {
      setPlay(true);
    }
  }, [loading, lifestyleScore]);

  return (
    <Screen
      className={css.root}
      hideHeader
      loading={loading || !hasSurveyAssessment}
      pageName="Lifestyle Summary Preview"
      pageData={{
        surveyId,
        surveyKey,
        surveyScore: lifestyleScore,
        categoryScores: previewCategories.reduce((acc, category) => {
          const score = getNormalizedLifestyleScore(
            category.categoryScore,
            category.maxCategoryScore,
          );
          const label = `${category.title}Score`;
          return {...acc, [label]: score};
        }, {}),
      }}
      withDefaultSizing={false}>
      <NavHeader />

      <Header
        lifestyleScore={play ? lifestyleScore || 0 : 0}
        maxLifestyleScore={maxLifestyleScore}
        hasActiveSubscription={hasActiveSubscription}
        registeredDate={registeredDate}
      />

      <main className={cx(sprinkles({layout: 'grid', pY: 64}), pageSlideInKeyframe)}>
        <div
          className={sprinkles({
            bg: 'burgundy',
            colSpan: [4, 6],
            colStart: [1, 2, 4],
            dir: 'col',
            flex: 'flex',
            gap: 24,
            mB: 64,
            p: 24,
            pos: 'relative',
            radius: 8,
          })}>
          <Text color="ecruWhite" variant="heading3">
            This is a partial summary
          </Text>

          <Text color="ecruWhite">
            {hasActiveSubscription
              ? 'Your TallyAge™ results will unlock additional content, including personalized recommendations and Action Plans. Check back in 2–4 weeks.'
              : 'Join Tally Health™ as a member to receive a full Lifestyle Summary, including recommendations designed to help you lower your TallyAge™.'}
          </Text>
        </div>

        <div
          className={sprinkles({
            colStart: [1, 2],
            colSpan: [4, 6, 3],
            flex: 'flex',
            dir: 'col',
            gap: 24,
            pB: [64, 64, 0],
          })}>
          <Text as="h2" color="deepBurgundy" variant="heading2">
            Lifestyle Overview
          </Text>

          <Text as="p" color="doveGray">
            Multiple health factors can impact your overall health in various ways and thus, impact
            your TallyAge™.
          </Text>
        </div>

        <CategoryList
          className={sprinkles({colStart: [1, 2, 6], colSpan: [4, 6]})}
          categories={previewCategories}
        />

        {!hasActiveSubscription && (
          <SubscriptionCTACard
            className={sprinkles({colSpan: [4, 8, 10], colStart: [1, 1, 2], mY: 64})}
          />
        )}
      </main>
    </Screen>
  );
};

export const LifestylePreview: NextPage = withRequireUserState(LifestylePreviewScreen, {
  userState: 'verified',
});
